import globalConfig from 'Helper/configHelpers'
import debug from 'Helper/debugHelpers'
import sha256 from 'js-sha256'
import { randomString, base64UrlEncode } from 'Helper/stringHelpers'
import { jsonResponseHandler, errorResponseHandler } from 'Helper/API/ResponseHandlers.js'

export const tokenRefresh = (refreshToken) => {
  const tokenUrl = `${globalConfig.api_base_url}/v1/oauth/token`

  const formData = new FormData()
  formData.append('refresh_token', refreshToken)
  formData.append('grant_type', 'refresh_token')
  formData.append('client_id', globalConfig.client_id)

  debug.log(`Making token refresh call to ${tokenUrl}`, debug.logLevels.AUTH)

  const headers = {
    Accept: 'application/json',
    xtractAppId: globalConfig.client_id,
    'X-CSRF-TOKEN': globalConfig.getCsrfToken()
  }

  const request = new Request(
    tokenUrl,
    {
      credentials: 'same-origin',
      method: 'POST',
      mode: 'cors',
      body: formData,
      cache: 'no-cache',
      headers: new Headers(headers)
    }
  )
  return fetch(request)
    .then(data => jsonResponseHandler(data))
    .catch(error => errorResponseHandler(error))
}

const arrayBufferToBase64 = buffer => {
  let binary = ''
  const bytes = new Uint8Array(buffer)
  const len = bytes.byteLength
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return window.btoa(binary)
}

export const getProviderQueryParams = (queryParams) => {
  let providerQueryString = ''
  Object.keys(queryParams).forEach((key, idx) => {
    if (idx === 0) {
      providerQueryString += `"${key}=${queryParams[key]}`
    } else {
      providerQueryString += `&${key}=${queryParams[key]}`
    }
  })
  if(providerQueryString != '') {
    providerQueryString += '"'
    providerQueryString = encodeURIComponent(providerQueryString)
  }
  return providerQueryString
}

export const authProviderLogin = (queryParams, successCallback, failureCallback) => {
  // start authorization grant
    // if successfull and returned user matches authProvider
        // call oauth protected api route to do the patient create/update and context calculation
        // change to resulting context.
    // if not successfull
  const providerQueryString = getProviderQueryParams(queryParams)
  getAuthCodeUrl('summit', '', queryParams, failureCallback, (data) => successCallback(data, providerQueryString))
}

export const getAuthCodeUrl = (AuthProvider = '', SubProvider = '', queryParams = {}, summitFailureCallback = () => null, summitSuccessCallback = () => null) => {
  if (SubProvider !== '') {
    SubProvider = `/${SubProvider}`
  }
  const clientId = globalConfig.client_id

  const authorizationUrl = `${globalConfig.api_base_url}/v1/oauth/authorize`
  const scope = '*'
  const redirectUri = globalConfig.api_base_url.replace('http:', 'https:')
    .replace('/api', `${SubProvider}/${AuthProvider}/callback`)

  const verifier = base64UrlEncode(randomString(128))
  sessionStorage.setItem('verifier', verifier)
  const state = base64UrlEncode(randomString(40))
  sessionStorage.setItem('state', state)
  const challenge = base64UrlEncode(arrayBufferToBase64(sha256.arrayBuffer(verifier)))
  let url = `${authorizationUrl}?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&code_challenge=${challenge}&state=${state}&code_challenge_method=S256`

  // append query params so they are available for the api auth process.
  const providerQueryParams = getProviderQueryParams(queryParams)
  if(providerQueryParams != '') {
    url+=`&providerQueryString=${providerQueryParams}`
  }

  const headers = {
    xtractAppId: globalConfig.client_id,
    'X-CSRF-TOKEN': globalConfig.getCsrfToken()
  }

  const request = new Request(
    url,
    {
      credentials: 'same-origin', // must be same origin
      method: 'GET',
      mode: 'cors',
      cache: 'no-cache',
      headers: new Headers(headers)
    }
  )
  return fetch(request)
    .then(data => {
      if (data.redirected) {
        summitSuccessCallback(data)
      } else if ((data.status === 401 || data.status === 419)) {
        sessionStorage.removeItem('state')
        sessionStorage.removeItem('verifier')
        summitFailureCallback(providerQueryParams)
      } else {
        sessionStorage.removeItem('state')
        sessionStorage.removeItem('verifier')
        data.clone()
          .text()
          .then(response => {
            debug.logObject('Unknown auth error (status: ' + data.status + ')', response, debug.logLevels.AUTH)
          })
      }
    })
}

export function getAuthCodeToken(query, SubProvider = '', AuthProvider = '') {
  if (SubProvider !== '') {
    SubProvider = `/${SubProvider}`
  }
  const clientId = globalConfig.client_id
  const tokenUrl = `${globalConfig.api_base_url}/v1/oauth/token`

  const redirectUri = globalConfig.api_base_url.replace('http:', 'https:')
    .replace('/api', `${SubProvider}/${AuthProvider}/callback`)
  const { code, state: receivedState } = query

  const verifier = sessionStorage.getItem('verifier')
  if (verifier) {
    sessionStorage.removeItem('verifier')
  } else {
    throw new Error("Session storage is required.")
  }
  const state = sessionStorage.getItem('state')
  if (state) {
    sessionStorage.removeItem('state')
  }else {
    throw new Error("Session storage is required.")
  }

  if (state && receivedState && state !== receivedState) {
    throw new Error('Received state does not match. Please try again.')
  } else if (!state || !receivedState) {
    // they had a callback url in the browser but they weren't actually in an auth process so send them
    // to login which is likely what they wanted
    return
  }
  const formData = new FormData()
  formData.append('grant_type', 'authorization_code')
  formData.append('client_id', clientId)
  formData.append('code_verifier', verifier)
  formData.append('redirect_uri', redirectUri)
  formData.append('code', code)

  const headers = {
    Accept: 'application/json',
    xtractAppId: globalConfig.client_id,
    'X-CSRF-TOKEN': globalConfig.getCsrfToken()
  }

  const request = new Request(
    // baseUrl is forced to https no mater what to eliminate risk of sending
    // credentials in plain text over http due to misconfiguration.
    tokenUrl,
    {
      credentials: 'same-origin',
      method: 'POST',
      mode: 'cors',
      cache: 'no-cache',
      body: formData,
      headers: new Headers(headers)
    }
  )
  return fetch(request)
    .then(data => jsonResponseHandler(data))
    .catch(error => errorResponseHandler(error))
}
